import _get from 'lodash/get'
import React, { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { Pressable, ScrollView, Text, View } from 'react-native'
import { tv } from 'tailwind-variants'

import { mapLegacyIconToPhosphor } from '@nxt-insurance/constants'

import { useQuoteData } from '@flows-ui/services/QuoteDataContext'
import type { ComparisonTableData } from '@flows-ui/services/QuoteDataContext.types'
import { useTranslation } from '@flows-ui/utils/i18n'
import { EVENTS } from '@flows/configs/analytics/constants'
import type { ProductKey } from '@flows/index'
import { IMAGE } from '@ui-kit/assets/images/index.web'
import UiKitCoveragePlans from '@ui-kit/components/CoveragePlans'
import type { CoveragePlanOption } from '@ui-kit/components/CoveragePlans/types'
import PhosphorIcon from '@ui-kit/components/PhosphorIcon'
import Icon from '@ui-kit/components/SvgIcon/index.web'
import Table from '@ui-kit/components/Table/index.web'
import type { TableData } from '@ui-kit/components/Table/types'
import RadioCardSet from '@ui-kit/components/formComponents/RadioCardSet/index.web'
import type { RadioCardOptionWeb } from '@ui-kit/components/formComponents/RadioCardSet/types'
import { h3, h4 } from '@ui-kit/core/typography'

import { useCoveragePlanSelection } from './hooks'

const CoveragePlanIcon = ({ name, fallbackSrc }: { name: string; fallbackSrc: string }) => {
  const phosphorName = mapLegacyIconToPhosphor(name)
  if (phosphorName) {
    return <PhosphorIcon name={phosphorName} size={32} />
  }
  return <Icon imgSrc={fallbackSrc} />
}

type Props = {
  productKey: ProductKey
  isCoveragePlanSelectorHidden?: boolean
  comparisonPresentation?: 'modal' | 'accordion'
}

const resolveHeaderIcon = (
  icon: ComparisonTableData['header'][number]['icon']
): string | undefined => {
  if (!icon) {
    return undefined
  }
  // The personalization service returns icon as { small: { name }, medium: { name }, large: { name } }
  if (typeof icon === 'object' && icon !== null) {
    const name: string | undefined =
      _get(icon, 'medium.name') ?? _get(icon, 'small.name') ?? _get(icon, 'large.name')
    return name ?? undefined
  }
  // Fallback: already a string icon name
  if (typeof icon === 'string') {
    return icon
  }
  return undefined
}

const mapComparisonTable = (comparisonTable: ComparisonTableData): TableData => {
  const resolvedIcons = comparisonTable.header.map((item) => resolveHeaderIcon(item.icon))
  const header = comparisonTable.header.map((item, index) => ({
    key: item.key,
    title: item.title,
    icon: _get(resolvedIcons, [String(index)]),
  }))
  return {
    title: comparisonTable.title,
    header,
    categoryRows: comparisonTable.categoryRows,
  }
}

const toAccordionTable = (comparisonTable?: ComparisonTableData | null) =>
  comparisonTable ? mapComparisonTable(comparisonTable) : undefined

const toCoveragePlanOption = (option: RadioCardOptionWeb): CoveragePlanOption => ({
  value: String(option.value),
  title: option.title,
  price: option.subtitle ?? '',
  description: option.description,
  icon: option.icon,
})

const ComparisonTableModal = ({
  comparisonTable,
  onClose,
}: {
  comparisonTable: ComparisonTableData
  onClose: () => void
}) => {
  const { backdrop, dialog, scrollArea, closeRow } = comparisonModalStyles()

  useEffect(() => {
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [])

  return createPortal(
    <div
      role="button"
      tabIndex={0}
      className={backdrop()}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose()
        }
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          onClose()
        }
      }}
    >
      <View className={dialog()}>
        <ScrollView className={scrollArea()}>
          <View className={closeRow()}>
            <Pressable onPress={onClose}>
              <PhosphorIcon name="X" size={24} />
            </Pressable>
          </View>
          <Table data={mapComparisonTable(comparisonTable)} />
        </ScrollView>
      </View>
    </div>,
    document.body
  )
}

const coveragePlansStyles = tv({
  slots: {
    container: 'flex-col gap-y-5 pt-12',
    comparisonTrigger:
      'flex-row items-center justify-between rounded-lg border border-grey-200 px-4 py-4',
  },
})

const comparisonModalStyles = tv({
  slots: {
    backdrop: 'fixed inset-0 z-[9999] flex items-center justify-center bg-black/50',
    dialog: 'mx-2 max-h-[85vh] w-[96vw] max-w-5xl overflow-hidden rounded-xl bg-white',
    scrollArea: 'px-4 py-4',
    closeRow: 'mb-2 flex-row items-center justify-end',
  },
})

export const CoveragePlans = (props: Props) => {
  const styles = coveragePlansStyles()
  const { t } = useTranslation()
  const { productGeneration, priceData, premiumInterval, formatCurrency, trackEvent } =
    useQuoteData()
  const [showComparison, setShowComparison] = useState(false)

  const coveragePlans = useMemo(() => productGeneration?.coveragePlans ?? [], [productGeneration])
  const comparisonTable = productGeneration?.comparisonTable

  const { selectedPlan, selectCoveragePlan } = useCoveragePlanSelection({
    productKey: props.productKey,
    coveragePlans,
    defaultCoveragePlan: productGeneration?.defaultCoveragePlan ?? null,
  })

  const shouldShow = coveragePlans.length > 1 && !props.isCoveragePlanSelectorHidden
  if (!shouldShow) {
    return null
  }

  const intervalLabel = premiumInterval === 'P1Y' ? 'year' : 'month'
  const normalize = (value: unknown): string => {
    const str = typeof value === 'string' ? value : ''
    return str
      .trim()
      .toLowerCase()
      .replaceAll(/[^\da-z]/g, '')
  }

  const getPlanPriceSubtitle = (plan: (typeof coveragePlans)[number], planIndex: number) => {
    const productConfigs = priceData?.product_configurations ?? []
    if (!productConfigs.length) {
      return ''
    }

    const planKey = normalize(plan.itemKey?.current)
    const planTitle = normalize(plan.title)
    const byGuaranteeId = productConfigs.find((config) => {
      const guarantee = normalize(_get(config, 'guarantee_id'))
      if (!guarantee) {
        return false
      }
      return guarantee === planKey || guarantee === planTitle
    })
    const byConfigId = productConfigs.find(
      (config) =>
        typeof config.configuration_id === 'string' &&
        ((planKey && normalize(config.configuration_id).includes(planKey)) ||
          (planTitle && normalize(config.configuration_id).includes(planTitle)))
    )
    const byProductKey = productConfigs.filter((config) => config.product_key === props.productKey)
    const byIndex = byProductKey[planIndex]
    const selectedConfig =
      selectedPlan &&
      normalize(selectedPlan.itemKey?.current) === planKey &&
      byProductKey.length === 1
        ? byProductKey[0]
        : undefined

    // Important: avoid falling back to the same first config for every plan.
    const matchedConfig = byGuaranteeId ?? byConfigId ?? byIndex ?? selectedConfig

    const matchedPrice = matchedConfig?.prices?.find(
      (price) => price.premium_interval === premiumInterval
    )
    const grossPremium = Number.parseFloat(matchedPrice?.gross_premium ?? '')
    if (!Number.isFinite(grossPremium)) {
      return ''
    }

    return `${formatCurrency(grossPremium)} / ${intervalLabel}`
  }

  const resolvePlanIcon = (plan: (typeof coveragePlans)[number]): string => {
    const icon = plan.icon
    if (!icon) {
      return IMAGE.icons.large.couch_64
    }
    // Try name-based resolution (Sanity CMS icon references)
    for (const size of ['medium', 'large', 'small'] as const) {
      const name = _get(icon, [size, 'name'])
      if (name && _get(IMAGE.icons, [size, name])) {
        return _get(IMAGE.icons, [size, name])
      }
    }
    // Fallback to URL-based (if icon has url field directly)
    return (
      _get(icon, 'large.url') ||
      _get(icon, 'medium.url') ||
      _get(icon, 'small.url') ||
      IMAGE.icons.large.couch_64
    )
  }

  const resolvePlanIconName = (plan: (typeof coveragePlans)[number]): string => {
    for (const size of ['medium', 'large', 'small'] as const) {
      const name = _get(plan.icon, [size, 'name'])
      if (typeof name === 'string') {
        return name
      }
    }
    return 'couch_64'
  }

  const radioOptions: RadioCardOptionWeb[] = coveragePlans.map((plan, planIndex) => ({
    value: plan.itemKey?.current ?? plan._id,
    icon: <CoveragePlanIcon name={resolvePlanIconName(plan)} fallbackSrc={resolvePlanIcon(plan)} />,
    title: plan.title,
    subtitle: getPlanPriceSubtitle(plan, planIndex),
    description: plan.description ?? '',
  }))

  const onValueChange = (value: string | number | boolean) => {
    const plan = coveragePlans.find((p) => (p.itemKey?.current ?? p._id) === value)
    if (plan) {
      selectCoveragePlan(plan)
    }
  }

  if (props.comparisonPresentation === 'accordion') {
    return (
      <UiKitCoveragePlans
        plans={radioOptions.map(toCoveragePlanOption)}
        value={selectedPlan?.itemKey?.current ?? selectedPlan?._id}
        onValueChange={onValueChange}
        compareLabel={t('sharedFlows_common:steps.quote.coverageComparison.button')}
        comparisonTable={toAccordionTable(comparisonTable)}
        onComparisonToggle={(isExpanded) =>
          trackEvent(EVENTS.AccordionListItemToggled, {
            id: 'coverage_comparison_table',
            isExpanded,
          })
        }
      />
    )
  }

  return (
    <View className={styles.container()}>
      <Text className={h3()}>{t('sharedFlows_common:steps.quote.selectCoverage.title')}</Text>
      <RadioCardSet
        layout="compact"
        options={radioOptions}
        onValueChange={onValueChange}
        value={selectedPlan?.itemKey?.current ?? selectedPlan?._id}
      />

      {comparisonTable ? (
        <Pressable
          className={styles.comparisonTrigger()}
          onPress={() => {
            setShowComparison(true)
            trackEvent(EVENTS.PopupOpened, { id: 'coverage_comparison_table' })
          }}
        >
          <Text className={h4()}>
            {t('sharedFlows_common:steps.quote.coverageComparison.button')}
          </Text>
          <PhosphorIcon name="CaretRight" size={24} />
        </Pressable>
      ) : null}

      {showComparison && comparisonTable ? (
        <ComparisonTableModal
          comparisonTable={comparisonTable}
          onClose={() => setShowComparison(false)}
        />
      ) : null}
    </View>
  )
}
